/**
 * GuidedTour — Tour produit immersif multi-pages.
 *
 * Différences clés vs version précédente :
 * - Chaque étape peut spécifier `route` → le tour navigue automatiquement
 * - Effet "spotlight" (overlay noir avec trou animé sur la cible) au lieu d'un overlay plat
 * - Tooltip avec flèche qui pointe la cible et se repositionne au resize/scroll
 * - Design attractif : welcome screen "fun", progression visuelle, mascotte sparkle
 * - Skip toujours visible mais secondaire — bouton primaire "Suivant" en gradient
 *
 * Usage :
 *   <GuidedTour role={user.role} onComplete={() => setTourDone(true)} />
 */
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    X, ChevronRight, ChevronLeft, Sparkles, HelpCircle,
    PartyPopper, MapPin, Compass, Rocket
} from 'lucide-react';

// Inline H mark — brand cohesion (matches the new app icon v6)
const HomlyMark = ({ className = '' }) => (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
        <defs>
            <linearGradient id="hmGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#681793" />
                <stop offset="100%" stopColor="#FF914D" />
            </linearGradient>
        </defs>
        <rect width="64" height="64" rx="15" ry="15" fill="url(#hmGrad)" />
        <g fill="#FFFFFF">
            <rect x="14" y="11" width="11" height="42" rx="1.4" />
            <rect x="39" y="11" width="11" height="42" rx="1.4" />
            <rect x="14" y="27" width="36" height="10" rx="1.4" />
        </g>
    </svg>
);

// ═══════════════════════════════════════════════════════════════════
// STEPS — by role
// ═══════════════════════════════════════════════════════════════════
const buildAdminSteps = () => ([
    {
        kind: 'welcome',
        title: 'Bienvenue sur Homly 👋',
        content: "Vous voici dans la plateforme conçue pour rendre votre agence immobilière plus performante, sans saisie laborieuse.\n\nLaissez-vous guider 2 minutes — vous découvrirez l'essentiel pour démarrer rapidement.",
        cta: "C'est parti",
    },
    {
        route: '/admin',
        target: '[data-testid="kpi-strip"], [data-testid="dirigeant-cockpit"]',
        position: 'bottom',
        title: '🎯 Votre cockpit en temps réel',
        content: "C'est votre tour de contrôle : CA, mandats actifs, taux de transformation. Chaque chiffre est calculé automatiquement à partir de vos données — aucune saisie nécessaire.",
    },
    {
        route: '/admin/simulator',
        target: '[data-testid="impact-hero"], [data-testid="simulator-page"]',
        position: 'bottom',
        title: '✨ Le Diagnostic IA',
        content: "L'IA analyse votre activité et identifie 3 leviers concrets pour gagner du CA. Synthèse, signaux d'urgence, plan d'action 90 jours — exportable en PDF et partageable par lien.",
        waitForSelector: 8000,
    },
    {
        route: '/admin/kpi',
        target: '[data-testid="kpi-catalog-page"]',
        position: 'top',
        title: '📚 Vos KPIs, expliqués simplement',
        content: "Catalogue complet d'indicateurs métier. Cliquez sur n'importe quel KPI pour voir sa définition, comment il est calculé, et 3 conseils concrets pour l'améliorer.",
    },
    {
        route: '/admin/kpi',
        target: '[data-testid^="kpi-star-"]',
        position: 'right',
        title: '⭐ Suivez ce qui compte',
        content: "Cliquez sur l'étoile pour épingler vos KPIs prioritaires. Ils apparaissent ensuite tout en haut avec leur tendance 14 jours visible d'un coup d'œil.",
    },
    {
        route: '/admin/mandates',
        target: '[data-testid="mandates-list"], main',
        position: 'top',
        title: '📋 Vos mandats',
        content: "Tous les mandats de l'agence avec leur statut en temps réel. Cliquez sur un mandat pour ouvrir son détail enrichi : visites, offres, marché DVF, documents.",
    },
    {
        kind: 'finale',
        title: 'Tout est prêt 🚀',
        content: "Vous savez maintenant l'essentiel. À tout moment, cliquez sur le bouton « ? » en bas à gauche pour relancer ce guide.\n\nPour les mots techniques (DVF, compromis, mandat exclusif…), survolez les termes soulignés en pointillés — vous verrez leur définition.",
        cta: "Démarrer mon agence",
    },
]);

const buildAgentSteps = () => ([
    {
        kind: 'welcome',
        title: 'Bienvenue sur Homly 👋',
        content: "Votre cockpit personnel pour ne plus jamais perdre une vente. 90 secondes pour voir l'essentiel.",
        cta: "C'est parti",
    },
    {
        route: '/agent',
        target: '[data-testid="agent-cockpit"]',
        position: 'bottom',
        title: '🎯 Votre cockpit du jour',
        content: "Vos KPIs essentiels en haut, vos priorités du jour en dessous. Tout est calculé automatiquement à partir de vos mandats et activités.",
    },
    {
        route: '/agent',
        target: '[data-testid="priorities-panel"], [data-testid^="priority-"]',
        position: 'right',
        title: '🚨 Vos priorités du jour',
        content: "Notre IA analyse vos mandats et estimations et liste les actions urgentes : vendeur à rappeler, estimation à relancer, compte-rendu à faire. Plus jamais d'oubli.",
    },
    {
        route: '/agent/mandates',
        target: 'main',
        position: 'top',
        title: '📋 Vos mandats',
        content: "Liste de tous vos mandats avec leur statut. Cliquez pour voir le détail : visites, offres, comparatif marché DVF, documents.",
    },
    {
        route: '/agent/kpi',
        target: '[data-testid="kpi-catalog-page"]',
        position: 'top',
        title: '📚 Vos KPIs expliqués',
        content: "100 indicateurs métier avec leur définition et 3 conseils pour les améliorer. Épinglez ceux qui comptent (étoile) — ils s'afficheront avec leur tendance 14 jours.",
    },
    {
        kind: 'finale',
        title: 'Vous êtes prêt 🚀',
        content: "Le bouton « ? » en bas à gauche relance ce guide à tout moment.\n\nLes mots métier (mandat exclusif, compromis, DVF…) sont expliqués au survol des mots soulignés en pointillés.",
        cta: "Aller travailler",
    },
]);

const buildSellerSteps = () => ([
    {
        kind: 'welcome',
        title: 'Bienvenue sur votre espace 👋',
        content: "Suivez la vente de votre bien en toute transparence. 1 minute pour voir l'essentiel.",
        cta: "Découvrir",
    },
    {
        route: '/seller',
        target: 'main',
        position: 'bottom',
        title: '🏠 Votre tableau de bord',
        content: "Statut en temps réel de votre mandat : créé, signé, en vente, offre, compromis, vendu. Vous savez toujours où on en est.",
    },
    {
        kind: 'finale',
        title: 'À vous 🚀',
        content: "Vous pouvez contacter votre agent à tout moment depuis vos mandats, et le noter pendant et après la vente.",
        cta: "Voir mes mandats",
    },
]);

// ═══════════════════════════════════════════════════════════════════
// SPOTLIGHT — Overlay with animated hole on target
// ═══════════════════════════════════════════════════════════════════
function Spotlight({ rect, onClick }) {
    // Quand la cible n'a pas (encore) ete trouvee, on n'affiche AUCUN overlay
    // pour que la page reste pleinement lisible derriere le tooltip.
    // Le tooltip se positionne alors en bas-droite (cf. TourTooltip).
    if (!rect) {
        return null;
    }
    const padding = 8;
    const r = {
        x: rect.left - padding,
        y: rect.top - padding,
        w: rect.width + padding * 2,
        h: rect.height + padding * 2,
    };
    return (
        <motion.svg
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] pointer-events-auto"
            style={{ width: '100%', height: '100%' }}
            onClick={onClick}
        >
            <defs>
                <mask id="spotlight-mask">
                    <rect width="100%" height="100%" fill="white" />
                    <motion.rect
                        initial={false}
                        animate={{ x: r.x, y: r.y, width: r.w, height: r.h }}
                        transition={{ type: 'spring', stiffness: 200, damping: 28 }}
                        rx="12"
                        ry="12"
                        fill="black"
                    />
                </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.72)" mask="url(#spotlight-mask)" />
            {/* Gradient ring around the spotlight */}
            <motion.rect
                initial={false}
                animate={{ x: r.x - 2, y: r.y - 2, width: r.w + 4, height: r.h + 4 }}
                transition={{ type: 'spring', stiffness: 200, damping: 28 }}
                rx="14"
                ry="14"
                fill="none"
                stroke="url(#spotlight-stroke)"
                strokeWidth="2"
            />
            <defs>
                <linearGradient id="spotlight-stroke" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor="#FF914D" />
                    <stop offset="100%" stopColor="#681793" />
                </linearGradient>
            </defs>
        </motion.svg>
    );
}

// ═══════════════════════════════════════════════════════════════════
// TOOLTIP — Anchored to target with arrow
// ═══════════════════════════════════════════════════════════════════
function TourTooltip({ rect, position, children, isCenter }) {
    const tipRef = useRef(null);
    const [pos, setPos] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
    const [arrow, setArrow] = useState(null);

    useLayoutEffect(() => {
        if (isCenter) {
            setPos({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
            setArrow(null);
            return;
        }
        if (!rect) {
            // Cible introuvable (chargement async lent ou DOM masque) : on positionne
            // le tooltip en bas-droite pour garder la page visible et utilisable.
            setPos({ bottom: '24px', right: '24px', top: 'auto', left: 'auto', transform: 'none' });
            setArrow(null);
            return;
        }
        const tipW = 380;
        const tipH = tipRef.current?.offsetHeight || 280;
        const margin = 16;

        let top, left, arrowDir = position;
        if (position === 'right') {
            top = Math.max(margin, Math.min(rect.top + rect.height / 2 - tipH / 2, window.innerHeight - tipH - margin));
            left = rect.right + margin;
            if (left + tipW > window.innerWidth - margin) {
                left = Math.max(margin, rect.left - tipW - margin);
                arrowDir = 'left';
            }
        } else if (position === 'left') {
            top = Math.max(margin, Math.min(rect.top + rect.height / 2 - tipH / 2, window.innerHeight - tipH - margin));
            left = rect.left - tipW - margin;
            if (left < margin) {
                left = rect.right + margin;
                arrowDir = 'right';
            }
        } else if (position === 'top') {
            left = Math.max(margin, Math.min(rect.left + rect.width / 2 - tipW / 2, window.innerWidth - tipW - margin));
            top = rect.top - tipH - margin;
            if (top < margin) {
                top = rect.bottom + margin;
                arrowDir = 'bottom';
            }
        } else { // bottom
            left = Math.max(margin, Math.min(rect.left + rect.width / 2 - tipW / 2, window.innerWidth - tipW - margin));
            top = rect.bottom + margin;
            if (top + tipH > window.innerHeight - margin) {
                top = rect.top - tipH - margin;
                arrowDir = 'top';
            }
        }
        // Clamp
        top = Math.max(margin, Math.min(top, window.innerHeight - tipH - margin));
        left = Math.max(margin, Math.min(left, window.innerWidth - tipW - margin));
        setPos({ top: `${top}px`, left: `${left}px`, transform: 'none' });
        setArrow(arrowDir);
    }, [rect, position, isCenter, children]);

    return (
        <motion.div
            ref={tipRef}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.22 }}
            className="fixed z-[70] w-[min(380px,calc(100vw-32px))] pointer-events-auto"
            style={pos}
        >
            {/* Arrow */}
            {arrow && (
                <span
                    aria-hidden
                    className={`absolute w-3 h-3 bg-[#1a1a2e] border-[#FF914D]/40 transform rotate-45 ${
                        arrow === 'right' ? 'left-[-6px] top-1/2 -translate-y-1/2 border-l border-b' :
                        arrow === 'left' ? 'right-[-6px] top-1/2 -translate-y-1/2 border-r border-t' :
                        arrow === 'top' ? 'left-1/2 -translate-x-1/2 bottom-[-6px] border-r border-b' :
                        arrow === 'bottom' ? 'left-1/2 -translate-x-1/2 top-[-6px] border-l border-t' : ''
                    }`}
                />
            )}
            <div className="relative bg-[#1a1a2e] border border-[#FF914D]/40 rounded-2xl shadow-2xl shadow-[#681793]/30 overflow-hidden">
                {children}
            </div>
        </motion.div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN GUIDED TOUR
// ═══════════════════════════════════════════════════════════════════
export default function GuidedTour({ role, onComplete }) {
    const [active, setActive] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [rect, setRect] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    const steps = role === 'admin' ? buildAdminSteps()
                : role === 'agent' ? buildAgentSteps()
                : buildSellerSteps();
    const step = steps[stepIndex] || {};
    const isCenter = step.kind === 'welcome' || step.kind === 'finale' || !step.target;

    // First-visit auto-start
    useEffect(() => {
        const tourKey = `homly_tour_${role}_seen_v2`;
        if (!localStorage.getItem(tourKey)) {
            const t = setTimeout(() => {
                setActive(true);
                // Mark as 'started' immediately so if the user closes the browser
                // mid-tour the tour does not re-trigger on next login. Only proper
                // close() or completion overrides with 'skipped' / 'completed'.
                try { localStorage.setItem(tourKey, 'started'); } catch (e) { /* quota */ }
            }, 1200);
            return () => clearTimeout(t);
        }
    }, [role]);

    // Navigate to step's route if needed
    useEffect(() => {
        if (!active) return;
        if (step.route && location.pathname !== step.route) {
            navigate(step.route);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, stepIndex]);

    // Locate target & track its rect (with retries while page loads)
    useEffect(() => {
        if (!active || isCenter) {
            setRect(null);
            return;
        }
        let attempts = 0;
        const maxAttempts = step.waitForSelector ? Math.floor(step.waitForSelector / 200) : 50; // 10s default
        let raf;
        const tick = () => {
            const el = document.querySelector(step.target);
            if (el) {
                const r = el.getBoundingClientRect();
                if (r.width > 0 && r.height > 0) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
                    // Re-measure after scroll
                    setTimeout(() => {
                        const r2 = el.getBoundingClientRect();
                        setRect({ left: r2.left, top: r2.top, width: r2.width, height: r2.height, right: r2.right, bottom: r2.bottom });
                    }, 350);
                    return;
                }
            }
            attempts += 1;
            if (attempts < maxAttempts) {
                raf = setTimeout(tick, 200);
            } else {
                // Fallback : center the tooltip
                setRect(null);
            }
        };
        tick();
        return () => { if (raf) clearTimeout(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, stepIndex, location.pathname]);

    // Reposition on resize/scroll
    useEffect(() => {
        if (!active || isCenter) return;
        const update = () => {
            const el = document.querySelector(step.target);
            if (!el) return;
            const r = el.getBoundingClientRect();
            setRect({ left: r.left, top: r.top, width: r.width, height: r.height, right: r.right, bottom: r.bottom });
        };
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, stepIndex, isCenter]);

    const close = useCallback((completed = false) => {
        setActive(false);
        setStepIndex(0);
        const tourKey = `homly_tour_${role}_seen_v2`;
        localStorage.setItem(tourKey, completed ? 'completed' : 'skipped');
        if (completed) onComplete?.();
    }, [role, onComplete]);

    const next = () => {
        if (stepIndex >= steps.length - 1) close(true);
        else setStepIndex(i => i + 1);
    };
    const prev = () => {
        if (stepIndex > 0) setStepIndex(i => i - 1);
    };

    const restart = useCallback(() => {
        setStepIndex(0);
        setActive(true);
    }, []);
    useEffect(() => {
        window.__restartGuidedTour = restart;
        return () => { delete window.__restartGuidedTour; };
    }, [restart]);

    if (!active) return null;

    const isFirst = stepIndex === 0;
    const isLast = stepIndex === steps.length - 1;
    // Content steps = exclude welcome (kind='welcome') and finale (kind='finale')
    const contentSteps = steps.filter(s => s.kind !== 'welcome' && s.kind !== 'finale');
    const contentTotal = contentSteps.length;
    // Position of current step among content steps (1-based) — only meaningful when on a content step
    const contentPos = isCenter ? 0 : steps.slice(0, stepIndex + 1).filter(s => s.kind !== 'welcome' && s.kind !== 'finale').length;
    const progress = (contentPos / Math.max(contentTotal, 1)) * 100;

    // Welcome / finale = full center modal, no spotlight
    if (step.kind === 'welcome' || step.kind === 'finale') {
        return (
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-md flex items-center justify-center p-4"
                    data-testid="tour-modal"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92 }}
                        transition={{ type: 'spring', stiffness: 200, damping: 22 }}
                        className="relative w-full max-w-md bg-gradient-to-br from-[#1a1a2e] via-[#1a1a2e] to-[#2a1a3e] border border-[#FF914D]/30 rounded-3xl shadow-2xl shadow-[#681793]/40 overflow-hidden"
                    >
                        {/* Decorative blobs */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF914D]/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#681793]/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none" />
                        {/* Skip top right */}
                        <button
                            onClick={() => close(false)}
                            className="absolute top-4 right-4 z-10 text-white/40 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
                            data-testid="tour-skip"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <div className="relative px-7 py-8 text-center">
                            <motion.div
                                initial={{ scale: 0.6, rotate: -20 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ delay: 0.15, type: 'spring' }}
                                className="inline-flex items-center justify-center w-16 h-16 mb-4 relative"
                            >
                                {step.kind === 'welcome' ? (
                                    <>
                                        <HomlyMark className="w-16 h-16 drop-shadow-xl" />
                                        {/* subtle pulse halo */}
                                        <span className="absolute inset-0 rounded-2xl bg-[#FF914D]/40 blur-xl animate-pulse pointer-events-none" />
                                    </>
                                ) : (
                                    <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF914D] to-[#681793] shadow-xl shadow-[#FF914D]/30">
                                        <PartyPopper className="w-8 h-8 text-white" />
                                    </span>
                                )}
                            </motion.div>
                            <h2 className="text-2xl font-black text-white mb-3">{step.title}</h2>
                            <p className="text-sm text-white/70 leading-relaxed whitespace-pre-line">{step.content}</p>
                            <div className="mt-6 flex items-center justify-center gap-3">
                                <button
                                    onClick={() => close(false)}
                                    className="text-xs text-white/40 hover:text-white transition-colors"
                                    data-testid="tour-skip-link"
                                >
                                    Passer le tour
                                </button>
                                <button
                                    onClick={next}
                                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold text-sm shadow-lg shadow-[#FF914D]/20 hover:shadow-xl hover:shadow-[#FF914D]/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2"
                                    data-testid="tour-next"
                                >
                                    {step.cta || 'Suivant'}
                                    <Rocket className="w-4 h-4" />
                                </button>
                            </div>
                            {step.kind === 'welcome' && (
                                <p className="mt-4 text-[10px] text-white/30">
                                    {contentTotal} étape{contentTotal > 1 ? 's' : ''} · ~2 min · Ré-affichable via le bouton « ? »
                                </p>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        );
    }

    // In-app step : spotlight + anchored tooltip
    return (
        <AnimatePresence>
            <Spotlight rect={rect} onClick={() => { /* swallow */ }} />
            <TourTooltip rect={rect} position={step.position || 'bottom'} isCenter={false}>
                {/* Progress bar */}
                <div className="h-1 bg-white/10">
                    <motion.div
                        className="h-full bg-gradient-to-r from-[#681793] to-[#FF914D]"
                        initial={false}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.3 }}
                    />
                </div>
                <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white text-[11px] font-bold">
                                {contentPos}
                            </div>
                            <span className="text-[11px] text-white/40">étape {contentPos} / {contentTotal}</span>
                        </div>
                        <button
                            onClick={() => close(false)}
                            className="text-white/30 hover:text-white p-1 rounded transition-colors"
                            data-testid="tour-close"
                            title="Fermer le tour"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 leading-tight">{step.title}</h3>
                    <p className="text-[13px] text-white/75 leading-relaxed whitespace-pre-line">{step.content}</p>
                </div>
                <div className="px-5 pb-4 flex items-center justify-between gap-3">
                    {!isFirst ? (
                        <button
                            onClick={prev}
                            className="text-xs text-white/50 hover:text-white flex items-center gap-1 transition-colors"
                            data-testid="tour-prev"
                        >
                            <ChevronLeft className="w-3.5 h-3.5" />
                            Précédent
                        </button>
                    ) : <span />}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => close(false)}
                            className="text-[11px] text-white/35 hover:text-white/60 transition-colors"
                            data-testid="tour-skip-step"
                        >
                            Passer
                        </button>
                        <button
                            onClick={next}
                            className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold text-xs shadow-md shadow-[#FF914D]/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-1"
                            data-testid="tour-next"
                        >
                            {isLast ? 'Terminer' : 'Suivant'}
                            {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
                        </button>
                    </div>
                </div>
            </TourTooltip>
        </AnimatePresence>
    );
}

// ═══════════════════════════════════════════════════════════════════
// HELP BUTTON — floating, restart tour (DESKTOP ONLY pour éviter de
// masquer le texte de l'IA sur mobile. Sur mobile, on accède au tour
// via le MobileDrawer (item "Relancer le tutoriel").
// ═══════════════════════════════════════════════════════════════════
export function GuidedTourHelpButton() {
    const [pulse, setPulse] = useState(true);
    useEffect(() => {
        const t = setTimeout(() => setPulse(false), 8000);
        return () => clearTimeout(t);
    }, []);
    return (
        <button
            onClick={() => window.__restartGuidedTour?.()}
            className={`hidden md:flex fixed md:bottom-6 md:left-6 z-[75] w-12 h-12 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] text-white shadow-xl shadow-[#681793]/40 items-center justify-center transition-all hover:scale-110 group ${pulse ? 'animate-bounce' : ''}`}
            title="Relancer le guide de prise en main"
            data-testid="tour-help-button"
            aria-label="Relancer le guide"
        >
            <HelpCircle className="w-6 h-6" strokeWidth={2.4} />
            <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-white text-slate-900 text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                Relancer le guide
            </span>
            {pulse && <span className="absolute inset-0 rounded-full bg-[#FF914D]/40 animate-ping" />}
        </button>
    );
}
